import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import Avatar from '@mui/material/Avatar'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded'
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined'
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded'
import LogoutIcon from '@mui/icons-material/Logout'
import LanguageIcon from '@mui/icons-material/Language'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useThemeMode } from "@/theme/ThemeModeProvider";
import useLogout from "@/hooks/useLogout";
import useLanguage from "@/hooks/useLanguage";

const ProfileSettingsButton = () => {
  const logout = useLogout();
  const { t } = useTranslation();
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null);
  
  const onProfileClick = () => {
    setAnchorEl(null)
    navigate('/profile')
  }
  
  const onLogoutClick = () => {
    setAnchorEl(null)
    logout()
  }
  return (
  <>
    <Tooltip 
      title={ t('profile') }
      placement="right"  
    >
      <IconButton
        sx={{
          p: 1.25,
          m: 0.5,
          borderRadius: 2,
          marginRight: 'auto',
        }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <ManageAccountsIcon />
      </IconButton>
    </Tooltip>
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      slotProps={{
        list: {
          sx: {
            pt: 0,
            pb: 0,
            borderRadius: 1,
          }
        },
        paper: {
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            ml: 3,
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              bottom: 24,
              left: -5,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(+50%) rotate(135deg)',
              zIndex: 0,
            },
          },
        },
      }}
    >
      <MenuItem onClick={onProfileClick}>
        <ListItemIcon color={"error.main"}>
          <PersonOutlineIcon fontSize="small" />
        </ListItemIcon>
        {t('myProfile')}
      </MenuItem>
      <MenuItemLanguageSwitcher />
      <MenuItemThemeToggle />
      <MenuItem 
        color="error"
        onClick={onLogoutClick}
      >
        <ListItemIcon>
          <LogoutIcon 
            color="error"
            fontSize="small" 
          />
        </ListItemIcon>
        <ListItemText 
          slotProps={{
            primary: {
              sx: {
                color: (theme) => theme.palette.error.main,
              } 
            }
          }}
        >
          {t('logout')}
        </ListItemText>
      </MenuItem>
    </Menu>
  </>
  )
}

const MenuItemThemeToggle = () => {
  const { t } = useTranslation();
  const { mode, toggleColorMode } = useThemeMode()
  const isDark = mode === 'dark'
  const ActiveThemeIcon = isDark ? DarkModeRoundedIcon : LightModeRoundedIcon
  // const themeTitle = t(isDark ? 'dark_mode' : 'light_mode');
  return (
    <MenuItem onClick={toggleColorMode}>
      <ListItemIcon>
        <ActiveThemeIcon fontSize="small" />
      </ListItemIcon>
      {t('theme_mode')}
    </MenuItem>
  )
}

const MenuItemLanguageSwitcher = () => {
  const { langaugesList, currentLanguageCode, setLanguage } = useLanguage();
  const [anchorEl, setAnchorEl] = useState();
  const { t } = useTranslation();
  const onChange = (code) => {
    setLanguage(code);
    setAnchorEl(null)
  }
  return (
    <>
      <MenuItem
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <ListItemIcon>
          <LanguageIcon fontSize="small"/>
        </ListItemIcon>
        { t('language') }
        <ChevronRightIcon 
          fontSize="small"
          sx={{
            ml: 'auto',
          }}  
        />
      </MenuItem>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ 
          vertical: 'bottom',
          horizontal: 'right', 
        }}
        transformOrigin={{ 
          vertical: 'bottom',
          horizontal: 'left', 
        }}
      >
        {
          langaugesList.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => onChange(lang.code)}
              selected={lang.code === currentLanguageCode}
            >
              <ListItemIcon>
                <Avatar
                  src={lang.flagImg}
                  alt={lang.code}
                  sx={{ width: 18, height: 18 }}
                />
              </ListItemIcon>
              <ListItemText>{lang.label}</ListItemText>
            </MenuItem>
          ))
        }
      </Menu>
    </>
  )
}

export default ProfileSettingsButton;